// Summary API Types

export interface CountrySummary {
  country: string
  country_code: string
  spend: number
  revenue: number
  roas: number
  blended_roas: number
  campaigns: number
  active: number
}

// ProductDailyData represents daily metrics for a product
export interface ProductDailyData {
  date: string
  spend: number
  revenue: number
  roas: number
}

export interface ProductSummary {
  product: string
  product_code: string
  campaign_names: string[] // Sample campaign names for this product
  spend: number
  revenue: number
  roas: number
  avg_roas: number
  campaigns: number
  daily_data?: ProductDailyData[] // Daily breakdown for charts
}

export interface OverallPerformance {
  blended_roas: number
  spend: number
  gross_revenue: number
  gross_profit: number
  cvr: number
  orders: number
  impressions: number
  clicks: number
  ctr: number
  cpc: number
  cpm: number
  total_campaigns: number
  active_campaigns: number
}

export interface TrafficSourceSummary {
  source: string
  layer: string
  spend: number
  revenue: number
  roas: number
  campaigns: number
}

export interface SummaryMeta {
  country?: string
  start_date: string
  end_date: string
  total_campaigns?: number
  cogs_percentage?: number // 0.0 to 1.0 (e.g., 0.45 = 45%)
}

// DailySummary represents aggregated metrics for a single day (from API)
export interface DailySummary {
  date: string
  spend: number
  revenue: number
  blended_roas: number
  paid_media_roas: number
  gross_profit: number
  cvr: number
  impressions: number
  clicks: number
  conversions: number
}

// HourlyMetrics represents metrics for a specific hour
export interface HourlyMetrics {
  spend: number
  revenue: number
  blended_roas: number
  gross_profit: number
  cvr: number
  impressions: number
  clicks: number
  conversions: number
}

// HourlySummary represents hourly comparison data (yesterday vs today)
export interface HourlySummary {
  hour: number
  yesterday_total: HourlyMetrics
  yesterday: HourlyMetrics
  today: HourlyMetrics
}

// HourlyTotals for legend display
export interface HourlyTotals {
  yesterday_total: HourlyMetrics
  yesterday: HourlyMetrics
  today: HourlyMetrics
}

// HourlySummaryResponse from the /summary/hourly endpoint
export interface HourlySummaryResponse {
  hourly: HourlySummary[]
  totals: HourlyTotals
  meta: {
    current_hour: number
    yesterday: string
    today: string
  }
}

export interface FullSummaryResponse {
  countries: CountrySummary[]
  overall: OverallPerformance
  traffic_sources: TrafficSourceSummary[]
  products: ProductSummary[]
  daily: DailySummary[]
  meta: SummaryMeta
}

export interface DateRange {
  startDate: string
  endDate: string
}

// Country flag emoji mapping
export const countryFlags: Record<string, string> = {
  US: '🇺🇸',
  CA: '🇨🇦',
  AU: '🇦🇺',
  UK: '🇬🇧',
  NZ: '🇳🇿',
  SG: '🇸🇬',
  DK: '🇩🇰',
  FI: '🇫🇮',
  NL: '🇳🇱',
  SE: '🇸🇪',
  DE: '🇩🇪',
  WW: '🌍',
}

// Layer display names
export const layerNames: Record<string, string> = {
  PPT: 'Prospecting',
  RMK: 'Remarketing',
  SHM: 'Custom',
}
